import {analysisAreaTypes, analysisCategories, RcfaUserInfo, AnalysisInfo} from '@domain/rcfa'
import {RcfaStoppageCode as StoppageCode} from '@domain/shared'
import {StoppageSection, StoppageInfo} from '@domain/stoppages'
import {AnalysisPrivacyStatus, AnalysisType} from '@hconnect/common/types'
import {useUpmPlantsContext} from '@hconnect/upm'
import {AccessTime, EditNote, HideSource} from '@mui/icons-material'
import ArrowBack from '@mui/icons-material/ArrowBack'
import ArrowForward from '@mui/icons-material/ArrowForward'
import {
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Stack,
  Paper,
  Autocomplete,
  Divider,
  Link
} from '@mui/material'
import {DatePicker, DateTimePicker} from '@mui/x-date-pickers'
import {debounce, isString} from 'lodash'
import moment, {Moment} from 'moment'
import React, {ReactNode, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import ReactQuill from 'react-quill'
import {useNavigate} from 'react-router'

import {FEATURE_NAMESPACE} from '../../localization'
import {formatDateTime} from '../../utils'
import {
  nodeDescriptionValidation,
  nodeTitleValidation,
  shouldNotBeEmptyString,
  shouldNotBeInPast,
  validateAll
} from '../../utils/validator'
import {
  AnalysisDescriptionTooltip,
  AnalysisPrivacyStatusSwitch,
  RcfaUserSearch,
  SearchStoppageCode,
  usePlantUpmId
} from '../common'

import {AnalysisToCreate} from './analysisCreate.types'
import {useCreateAnalysis} from './useCreateAnalysis'
import {useGetStoppagesSuggestions} from './useGetStoppageSuggestion'

export interface CreateAnalysisFormProperties {
  onGoBack: () => void
  onGoNext: () => void
  onAnalysisCreated: (analysis: AnalysisInfo) => void
  selectedStoppage?: StoppageInfo
  analysisType?: AnalysisType
  canReassign: boolean
}

type CreateAnalysisItem = {
  title: string
  description: string
  privacyStatus: AnalysisPrivacyStatus
  stoppageStart: string | null
  stoppageEnd: string | null
  equipment: string | {text: string; id: string}
  owner: RcfaUserInfo | null
  category: string | null
  areaType: string | null
  dueDate: moment.Moment
}

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{list: 'ordered'}, {list: 'bullet'}, {indent: '-1'}, {indent: '+1'}],
    ['clean']
  ]
}

const getTextLength = (description: string | undefined) =>
  description?.length ? description.replaceAll(/(<([^>]+)>)/gi, '').length : 0

export type StoppageOption = {
  label: string
  id: string
  isStoppage: boolean
  icon?: ReactNode
  disabled?: boolean
}

export const CreateAnalysisForm: React.FC<CreateAnalysisFormProperties> = ({
  onGoBack,
  onGoNext,
  selectedStoppage,
  analysisType,
  onAnalysisCreated,
  canReassign
}) => {
  const navigate = useNavigate()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {mutate, isPending: isLoading} = useCreateAnalysis()
  const {data: stoppagesData, isPending: isLoadingStoppages} = useGetStoppagesSuggestions()

  const [item, setItem] = useState<CreateAnalysisItem>({
    title: selectedStoppage?.title ?? '',
    description: selectedStoppage?.description ?? '',
    privacyStatus: AnalysisPrivacyStatus.Public,
    stoppageStart: selectedStoppage?.start ?? null,
    stoppageEnd: selectedStoppage?.end ?? null,
    equipment: selectedStoppage?.equipment ?? '',
    owner: null,
    category: null,
    areaType: null,
    dueDate: moment().add(30, 'days')
  })
  const [customStoppage, setCustomStoppage] = useState<StoppageCode | null>(null)
  const [validationError, setValidationError] = useState(new Map())
  const [isWithStoppage, setIsWithStoppage] = useState<boolean>(false)
  const [fieldsDisabled, setFieldsDisabled] = useState<boolean>(true)
  const [externalStoppage, setExternalStoppage] = useState<StoppageInfo>()
  const [stoppages, setStoppages] = useState<StoppageInfo[]>([])
  const plantUpmId = usePlantUpmId()
  const upmPlants = useUpmPlantsContext()

  const [sOption, setSOption] = useState<StoppageOption | null>(null)

  useEffect(() => {
    if (stoppagesData) {
      setStoppages((s) => (s.length ? [...s] : [...stoppagesData, ...s]))
    }
  }, [stoppagesData])

  useEffect(() => {
    if (selectedStoppage) {
      setExternalStoppage(selectedStoppage)
      setFieldsDisabled(false)
      setIsWithStoppage(true)
      setSOption({
        id: selectedStoppage.id,
        label: `${selectedStoppage.stoppageCode?.type} - ${selectedStoppage.stoppageCode
          ?.code} - ${moment(selectedStoppage.start).format('DD.MM.YYYY HH:mm')} - ${moment(
          selectedStoppage.end
        ).format('DD.MM.YYYY HH:mm')}`,
        isStoppage: true
      })
      setStoppages((s) =>
        !s.findIndex((t) => t.id === selectedStoppage.id) ? [...s, selectedStoppage] : [...s]
      )
    }
  }, [selectedStoppage])

  const validatorConfig = new Map([
    ['description', shouldNotBeEmptyString],
    ['description', nodeDescriptionValidation],
    ['title', nodeTitleValidation],
    ['dueDate', shouldNotBeInPast]
  ])

  const debouncedMutate = useMemo(() => debounce(mutate, 500), [mutate])

  const buildOptionalParams = (createObject: AnalysisToCreate): AnalysisToCreate => {
    if (externalStoppage?.stoppageCode?.code || customStoppage?.code) {
      createObject.stoppageCode = externalStoppage?.stoppageCode?.code ?? customStoppage?.code
      createObject.stoppageCodeType = externalStoppage?.stoppageCode?.type ?? customStoppage?.type
    }
    if (externalStoppage?.id) createObject.externalGroupId = externalStoppage?.externalGroupId
    if (item.stoppageStart) createObject.stoppageStart = item.stoppageStart
    if (item.stoppageEnd) createObject.stoppageEnd = item.stoppageEnd
    if (item.owner) createObject.owner = item.owner.id
    if (item.category) createObject.category = item.category
    if (item.areaType) createObject.areaTypeCode = item.areaType
    if (item.equipment)
      createObject.equipmentName =
        typeof item.equipment === 'string' ? item.equipment : item.equipment.text

    return createObject
  }

  const doSubmit = () => {
    const validationError = validateAll(validatorConfig, item)
    setValidationError(validationError)
    if (validationError.size === 0 && !moment(item.dueDate).isBefore(moment().startOf('day'))) {
      const createObject: AnalysisToCreate = {
        title: item.title,
        description: item.description,
        privacyStatus: item.privacyStatus,
        type: analysisType,
        plantUpmId: externalStoppage?.plantId
          ? upmPlants[externalStoppage?.plantId]?.upmId
          : plantUpmId,
        dueDate: item.dueDate.toISOString()
      }

      debouncedMutate(buildOptionalParams(createObject), {
        onSuccess: (data) => {
          onAnalysisCreated(data)
          onGoNext()
        }
      })
    }
  }

  const getErrorText = (key: string) => {
    const errorKey = validationError.get(key)
    return errorKey && t(errorKey)
  }

  const displayStoppageSelect = useMemo(
    () => analysisType === AnalysisType.Custom && isWithStoppage,
    [analysisType, isWithStoppage]
  )

  const setOwner = (assignee) => {
    setItem((item) => ({...item, owner: assignee}))
  }

  const stoppagesOptions = useMemo(() => {
    const customOptions: StoppageOption[] = [
      {
        label: 'Link to stoppage later',
        id: 'fill_later',
        isStoppage: false,
        icon: <AccessTime />
      },
      {
        label: 'This RCFA does not involve a stoppage',
        id: 'no_stoppage',
        isStoppage: false,
        icon: <HideSource />
      },
      {
        label: 'Add custom stoppage',
        id: 'custom_stoppage',
        isStoppage: false,
        icon: <EditNote />
      }
    ]

    let options: StoppageOption[] = []

    if (stoppages) {
      options = stoppages.map((s) => ({
        label: `${s.stoppageCode?.type} - ${s.stoppageCode?.code} - ${moment(s.start).format(
          'DD.MM.YYYY HH:mm'
        )} - ${moment(s.end).format('DD.MM.YYYY HH:mm')}`,
        id: s.id,
        isStoppage: true
      }))
    }

    return [...options, ...customOptions]
  }, [stoppages])

  const fillExternalStoppage = (id: string) => {
    const stoppage = stoppages?.find((s) => s.id === id)
    if (stoppage) {
      setExternalStoppage(stoppage)
      setItem({
        ...item,
        title: stoppage?.title ?? '',
        description: stoppage?.description ?? '',
        stoppageStart: stoppage?.start ?? null,
        stoppageEnd: stoppage?.end ?? null,
        equipment: stoppage?.equipment ?? ''
      })
    }
  }

  const selectStoppage = (id: string | undefined) => {
    if (id) {
      switch (id) {
        case 'fill_later':
        case 'no_stoppage':
          setIsWithStoppage(false)
          setFieldsDisabled(false)
          setExternalStoppage(undefined)
          break
        case 'custom_stoppage':
          setIsWithStoppage(true)
          setFieldsDisabled(false)
          setExternalStoppage(undefined)
          break
        default:
          fillExternalStoppage(id)
          setFieldsDisabled(false)
          setIsWithStoppage(false)
      }
    } else {
      setFieldsDisabled(true)
      setIsWithStoppage(false)
      setExternalStoppage(undefined)
    }
  }

  return (
    <Paper
      id="create-analysis-form"
      sx={{
        width: 490,
        height: 'fit-content',
        padding: 3
      }}
    >
      <Stack spacing={2.5}>
        <Typography variant={'h3'} sx={{fontSize: 18, flexGrow: 1, overflow: 'hidden', mb: 1}}>
          {t('cards.defineDetails')}
        </Typography>
        <Autocomplete
          key={'stoppage'}
          id="stoppage"
          groupBy={(option) => (option.isStoppage ? 'Suggested' : '')}
          options={stoppagesOptions}
          onChange={(_, stoppage) => {
            setSOption(stoppage)
            selectStoppage(stoppage?.id)
          }}
          value={sOption}
          data-test-id={'stoppage-selector'}
          loading={isLoadingStoppages}
          sx={{
            marginBottom: 2,
            maxWidth: 450
          }}
          renderOption={(props, option) => {
            return (
              <Box component={'li'} {...props}>
                <Stack width={'100%'}>
                  <Stack direction={'row'} gap={1} p={1} pl={0}>
                    {option.icon ?? <></>}
                    {option.label}
                  </Stack>
                  {option.id !== 'custom_stoppage' && <Divider />}
                </Stack>
              </Box>
            )
          }}
          renderInput={(params) => (
            <TextField {...params} label={'Stoppage *'} placeholder={'Select stoppage'} />
          )}
          PaperComponent={({children}) => {
            return (
              <Paper>
                {children}
                <Box
                  p={2}
                  textAlign={'center'}
                  onMouseDown={async (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    await navigate('../stoppages')
                  }}
                >
                  <Link
                    href={'#'}
                    onMouseDown={async (e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      await navigate('../stoppages')
                    }}
                  >
                    Go to stoppage list
                  </Link>
                </Box>
              </Paper>
            )
          }}
        />

        <TextField
          data-test-id="node-form-title"
          error={validationError.has('title')}
          fullWidth={true}
          helperText={getErrorText('title')}
          label={t('label.analysisTitle')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setItem((item) => ({...item, title: event.target.value}))
          }
          value={item.title}
          defaultValue={externalStoppage?.title}
          required
          disabled={fieldsDisabled}
        />
        <Autocomplete
          key={'analysis-category'}
          id="analysis-category"
          options={analysisCategories}
          onChange={(_, category) =>
            setItem((item) => ({
              ...item,
              category: category?.id || category
            }))
          }
          sx={{
            marginBottom: 2,
            maxWidth: 450
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('label.searchAnalysisCategoryLabel')}
              placeholder={t('label.searchAnalysisCategoryPlaceholder')}
            />
          )}
          disabled={fieldsDisabled}
        />
        <Autocomplete
          key={'analysis-areaType'}
          id="analysis-areaType"
          options={analysisAreaTypes}
          onChange={(e, category) =>
            setItem((item) => ({
              ...item,
              areaType: isString(category) ? category : category ? category.id : category
            }))
          }
          sx={{
            marginBottom: 2,
            maxWidth: 450
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('label.searchAnalysisAreaTypeLabel')}
              placeholder={t('label.searchAnalysisAreaTypePlaceholder')}
            />
          )}
          disabled={fieldsDisabled}
        />
        {displayStoppageSelect && (
          <>
            <SearchStoppageCode
              value={customStoppage}
              onChange={(stoppage) => setCustomStoppage(stoppage)}
            />
            <TextField
              id="equipment"
              data-test-id="stoppage-equipment-input"
              label={t('analysis.new.equipment')}
              onChange={(e) => {
                setItem((item) => ({
                  ...item,
                  equipment: e.target.value
                }))
              }}
            />
            <Stack direction="row" spacing={2}>
              <DateTimePicker
                inputFormat="DD.MM.YYYY HH:mm"
                ampm={false}
                data-test-id="stoppage-start-input"
                label={t('analysis.new.stoppageStart')}
                onChange={(value: Moment | null) => {
                  setItem((item) => ({
                    ...item,
                    stoppageStart: value ? value.toISOString() : value
                  }))
                }}
                value={item.stoppageStart}
                disableFuture
                renderInput={(params) => <TextField variant="filled" size="small" {...params} />}
              />
              <DateTimePicker
                inputFormat="DD.MM.YYYY HH:mm"
                ampm={false}
                data-test-id="stoppage-end-input"
                label={t('analysis.new.stoppageEnd')}
                onChange={(value: Moment | null) => {
                  setItem((item) => ({
                    ...item,
                    stoppageEnd: value ? value.toISOString() : value
                  }))
                }}
                value={item.stoppageEnd}
                disableFuture
                renderInput={(params) => <TextField variant="filled" size="small" {...params} />}
              />
            </Stack>
          </>
        )}
        {externalStoppage && (
          <Stack spacing={2} sx={{pl: 2}}>
            <StoppageSection
              label={t('analysis.new.equipment')}
              value={`${externalStoppage?.equipment?.text}`}
              valueProps={{sx: {textTransform: 'none'}}}
              captionProps={{sx: {textTransform: 'capitalize'}}}
            />
            <StoppageSection
              label={t('stoppage.detail.stoppageCode')}
              value={`${externalStoppage?.stoppageCode?.code} - ${externalStoppage?.stoppageCode?.text}`}
              valueProps={{sx: {textTransform: 'none'}}}
              captionProps={{sx: {textTransform: 'capitalize'}}}
            />
            <StoppageSection
              label={t('stoppage.detail.time')}
              value={`${externalStoppage.start ? formatDateTime(externalStoppage.start) : ''} - ${
                externalStoppage.end ? formatDateTime(externalStoppage.end) : ''
              }`}
              valueProps={{sx: {textTransform: 'none'}}}
              captionProps={{sx: {textTransform: 'capitalize'}}}
            />
          </Stack>
        )}
        <DatePicker
          value={item.dueDate}
          label={'Due Date'}
          inputFormat="DD.MM.YYYY"
          views={['day']}
          disablePast
          onChange={(newDueDate) =>
            newDueDate && setItem((item) => ({...item, dueDate: newDueDate}))
          }
          disabled={fieldsDisabled}
          renderInput={(params) => (
            <TextField
              {...params}
              helperText={validationError.get('dueDate') && t('task.dueDateIsInThePast')}
              sx={{
                width: '100%',
                marginBottom: validationError.get('dueDate') ? 3 : 2
              }}
            />
          )}
        />
        <Box mb={3} style={{display: 'flex', flexDirection: 'column'}}>
          <AnalysisDescriptionTooltip top={30} left={415} />
          <ReactQuill
            modules={modules}
            theme={'snow'}
            onChange={(next) => setItem((i) => ({...i, description: next}))}
            placeholder={`${t('label.description')} *`}
            formats={['bold', 'italic', 'underline', 'list', 'bullet', 'indent']}
            readOnly={fieldsDisabled}
            value={item.description}
          />
          {validationError.has('description') && (
            <Box ml={1} sx={{float: 'left'}}>
              <Typography sx={(theme) => ({color: theme.palette.error.light, fontSize: 12})}>
                {getErrorText('description')}
              </Typography>
            </Box>
          )}
          <Box sx={(theme) => ({color: theme.palette.grey['300'], alignSelf: 'flex-end'})}>
            {getTextLength(item.description)}/{5000}
          </Box>
        </Box>
        {canReassign && (
          <RcfaUserSearch
            assignee={item.owner ?? null}
            label={t('analysis.changeOwner.owner')}
            onChange={setOwner}
            legacyDesign
            disabled={fieldsDisabled}
          />
        )}
        <AnalysisPrivacyStatusSwitch
          value={item.privacyStatus}
          onChange={(privacyStatus) => setItem((item) => ({...item, privacyStatus}))}
        />
        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ArrowBack />}
            onClick={onGoBack}
            data-test-id="create-analysis-cancel-button"
          >
            {t('buttons.back')}
          </Button>
          <Button
            color="primary"
            disabled={isLoading}
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={doSubmit}
            data-test-id="create-analysis-save-button"
            id="create-analysis-save-button"
          >
            {isLoading ? (
              <CircularProgress sx={{color: 'white'}} size={28} />
            ) : (
              t('buttons.startAnalysis')
            )}
          </Button>
        </Box>
      </Stack>
    </Paper>
  )
}
